/**
 * FitCraft Studio 主入口文件
 * 实现模块按需加载、Service Worker注册和用户体验优化
 */

// 应用配置和工具
import { configManager } from './config/app-config.js';
import { errorHandler } from './utils/error-handler.js';

// Supabase client import - 优化为按需导入以减少打包体积
import { createClient } from '@supabase/supabase-js';

// Tailwind CSS 样式
import './styles.css';

// 懒加载组件
import '../js/modules/ui/lazy-loading.js';

// UI组件
import '../js/modules/ui/faqs-toggle.js';

// 客户端监控 - 已改为动态加载以优化性能

// 日志系统
import '../js/modules/core/logger.js';

// 用户体验优化模块
import '../js/modules/ux/ux-optimizer.js';

// 设置全局引用
window.appConfig = configManager.getConfig();
window.errorHandler = errorHandler;
window.configManager = configManager;

// 初始化Supabase客户端
const config = configManager.getConfig();
if (config.SUPABASE_URL && config.SUPABASE_ANON_KEY) {
  const supabaseClient = createClient(config.SUPABASE_URL, config.SUPABASE_ANON_KEY);

  // 为了兼容性，设置两个引用
  window.supabase = supabaseClient; // 新的标准引用
  window._supabase = supabaseClient; // 旧的引用，为了兼容现有代码
  window.createSupabaseClient = createClient; // 保留原API

  console.log('[Main] Supabase client initialized successfully');
} else {
  console.warn(
    '[Main] Supabase configuration missing - SUPABASE_URL or SUPABASE_ANON_KEY not found'
  );
}

// 创建环境相关的日志函数
const isDevelopment = configManager.isDevelopment();
// const debugLog = isDevelopment ? console.log.bind(console) : () => {}; // Available if needed
const debugInfo = isDevelopment ? console.info.bind(console) : () => {};
const debugWarn = isDevelopment ? console.warn.bind(console) : () => {};
const debugError = isDevelopment ? console.error.bind(console) : () => {};

/**
 * 模块加载管理器
 */
class ModuleLoader {
  constructor() {
    this.loadedModules = new Set();
    this.loadingPromises = new Map();
    this.init();
  }

  /**
   * 初始化模块加载器
   */
  init() {
    if (window.logger) {
      window.logger.info('[ModuleLoader] Initializing...');
    }

    // 初始化客户端监控
    this.initializeMonitoring();

    // 注册Service Worker
    this.registerServiceWorker();

    // 预加载核心模块
    this.preloadCoreModules();

    // 设置按需加载监听器
    this.setupLazyLoading();

    // 检查支付成功状态
    this.checkPaymentSuccess();

    debugInfo('[ModuleLoader] Initialized successfully');
  }

  /**
   * 初始化客户端监控
   * 使用动态导入延迟加载监控模块,减少初始bundle大小
   */
  async initializeMonitoring() {
    try {
      // 动态导入客户端监控模块
      const { default: clientMonitoring } = await import(
        /* webpackChunkName: "client-monitoring" */
        '../js/modules/monitoring/client-monitoring.js'
      );

      if (clientMonitoring && typeof clientMonitoring.initialize === 'function') {
        await clientMonitoring.initialize();
        debugInfo('[ModuleLoader] Client monitoring initialized');

        // 监听模块加载事件并追踪
        document.addEventListener('moduleLoaded', event => {
          try {
            clientMonitoring.trackEvent('module_loaded', {
              moduleGroup: event.detail.moduleGroup,
              loadTime: event.detail.loadTime,
            });
          } catch (trackError) {
            debugWarn('[ModuleLoader] Failed to track module load event:', trackError);
          }
        });
      }
    } catch (error) {
      debugError('[ModuleLoader] Failed to initialize monitoring:', error);

      // 监控初始化失败不应该影响用户体验,只记录错误
      if (window.errorHandler) {
        errorHandler.handleError(error, {
          userFriendlyMessage: null, // 不显示给用户
          showUserNotification: false,
        });
      }
    }
  }

  /**
   * 检测是否为移动设备
   */
  isMobileDevice() {
    // 检测触摸设备
    const hasTouchScreen =
      'ontouchstart' in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0;

    // 检测屏幕尺寸
    const isMobileScreen = window.innerWidth <= 768;

    // 检测User Agent
    const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
    const isMobileUA = mobileRegex.test(navigator.userAgent);

    // 综合判断
    return hasTouchScreen && (isMobileScreen || isMobileUA);
  }

  /**
   * 注册Service Worker
   */
  async registerServiceWorker() {
    // 只在移动设备上启用 (生产环境或开发环境都可以)
    if ('serviceWorker' in navigator && this.isMobileDevice()) {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js');

        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // 新版本可用，询问用户是否更新
              this.showUpdateNotification();
            }
          });
        });

        debugInfo('[SW] Service Worker registered successfully');
      } catch (error) {
        debugError('[SW] Service Worker registration failed:', error);

        // 用户友好的错误提示
        if (window.errorHandler) {
          errorHandler.handleError(error, {
            userFriendlyMessage: '应用缓存功能初始化失败，但不影响正常使用',
            showUserNotification: false, // SW错误不需要打扰用户
          });
        }
      }
    }
  }

  /**
   * 显示更新通知
   */
  showUpdateNotification() {
    if (confirm('应用有新版本可用，是否立即更新？\nA new version is available. Update now?')) {
      window.location.reload();
    }
  }

  /**
   * 预加载核心模块
   */
  async preloadCoreModules() {
    const coreModules = [
      () => import('../js/modules/core/utils.js'),
      () => import('../js/modules/ui/language-switcher.js'),
    ];

    try {
      await Promise.all(coreModules.map(loader => loader()));
      debugInfo('[ModuleLoader] Core modules loaded');
    } catch (error) {
      debugError('[ModuleLoader] Failed to load core modules:', error);

      if (window.errorHandler) {
        errorHandler.handleError(error, {
          userFriendlyMessage: '应用核心功能加载失败，可能影响部分功能正常使用',
          showUserNotification: true,
        });
      }
    }
  }

  /**
   * 设置按需加载监听器
   */
  setupLazyLoading() {
    // 监听登录按钮点击
    this.setupAuthModuleLoading();

    // 监听试穿按钮点击
    this.setupTryonModuleLoading();

    // 监听定价模态框
    this.setupPricingModuleLoading();

    // 法律模态框由 legal-modals.js 直接处理，无需懒加载
    // this.setupLegalModuleLoading();
  }

  /**
   * 检查支付成功状态并处理
   */
  checkPaymentSuccess() {
    try {
      const paymentSuccessData = sessionStorage.getItem('payment_success');
      if (paymentSuccessData) {
        const paymentInfo = JSON.parse(paymentSuccessData);

        // 检查是否是最近5分钟内的支付成功（避免处理过期数据）
        const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
        if (paymentInfo.timestamp > fiveMinutesAgo) {
          debugInfo('[Payment] Processing successful payment:', paymentInfo);

          // 显示支付成功提示
          this.showPaymentSuccessNotification(paymentInfo);

          // 刷新用户状态和额度信息
          this.refreshUserDataAfterPayment();
        }

        // 清除支付成功数据（无论是否处理）
        sessionStorage.removeItem('payment_success');
      }
    } catch (error) {
      debugError('[Payment] Error processing payment success:', error);
    }
  }

  /**
   * 显示支付成功通知
   */
  showPaymentSuccessNotification(_paymentInfo) {
    // 创建成功通知
    const notification = document.createElement('div');
    notification.className =
      'fixed top-4 right-4 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg z-50 transition-opacity duration-300';
    notification.innerHTML = `
      <div class="flex items-center">
        <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
        </svg>
        <span>支付成功！您的订阅已激活。</span>
      </div>
    `;

    document.body.appendChild(notification);

    // 3秒后自动消失
    setTimeout(() => {
      notification.style.opacity = '0';
      setTimeout(() => {
        notification.remove();
      }, 300);
    }, 3000);
  }

  /**
   * 支付成功后刷新用户数据
   */
  async refreshUserDataAfterPayment() {
    try {
      // 等待认证模块加载
      await this.loadAuthModules();

      // 刷新用户登录状态和额度信息
      if (window._supabase && window.queryUserRemainingQuota) {
        const {
          data: { user },
        } = await window._supabase.auth.getUser();
        if (user) {
          // 清除缓存
          if (window.quotaQueryCache) {
            window.quotaQueryCache.clear();
          }

          // 重新查询用户信息
          const updatedQuotaInfo = await window.queryUserRemainingQuota(user);
          if (updatedQuotaInfo) {
            window.userQuotaInfo = updatedQuotaInfo;
          }

          debugInfo('[Payment] User data refreshed after payment');
        }
      }
    } catch (error) {
      debugError('[Payment] Error refreshing user data:', error);
    }
  }

  /**
   * 设置认证模块加载
   */
  setupAuthModuleLoading() {
    const authTriggers = document.querySelectorAll(
      '#login-button, #logout-button, [data-auth-trigger]'
    );

    authTriggers.forEach(trigger => {
      // 使用标志位而不是once，避免重复加载
      let isLoading = false;
      let isLoaded = false;

      trigger.addEventListener('click', async e => {
        // 如果模块已加载，让事件正常执行
        if (isLoaded) {
          return;
        }

        // 如果正在加载，阻止重复点击
        if (isLoading) {
          e.preventDefault();
          return;
        }

        // 首次点击，需要加载模块
        e.preventDefault();
        isLoading = true;

        try {
          await this.loadAuthModules();
          isLoaded = true;
          isLoading = false;

          // 模块加载完成后，重新触发点击事件
          setTimeout(() => {
            trigger.click();
          }, 100);
        } catch (error) {
          isLoading = false;
          // 模块加载失败时，显示友好的错误提示但不重新触发点击
          debugError('[ModuleLoader] Auth module loading failed in click handler:', error);
        }
      });
    });

    // 预加载策略：鼠标悬停时预加载
    authTriggers.forEach(trigger => {
      trigger.addEventListener(
        'mouseenter',
        () => {
          this.preloadAuthModules();
        },
        { once: true }
      );
    });
  }

  /**
   * 设置试穿模块加载
   */
  setupTryonModuleLoading() {
    const tryonTriggers = document.querySelectorAll('#tryon-button, [data-tryon-trigger]');

    tryonTriggers.forEach(trigger => {
      // 使用标志位而不是once，避免重复加载
      let isLoading = false;
      let isLoaded = false;

      trigger.addEventListener('click', async e => {
        // 如果模块已加载，让事件正常执行
        if (isLoaded) {
          return;
        }

        // 如果正在加载，阻止重复点击
        if (isLoading) {
          e.preventDefault();
          return;
        }

        // 首次点击，需要加载模块
        e.preventDefault();
        isLoading = true;

        try {
          await this.loadTryonModules();
          isLoaded = true;
          isLoading = false;

          // 模块加载完成后，重新触发点击事件
          setTimeout(() => {
            trigger.click();
          }, 100);
        } catch (error) {
          isLoading = false;
          // 试穿模块加载失败时的处理
          debugError('[ModuleLoader] Tryon module loading failed in click handler:', error);
        }
      });
    });

    // 图片上传区域交互时预加载
    const uploadAreas = document.querySelectorAll('.upload-area');
    uploadAreas.forEach(area => {
      area.addEventListener(
        'dragenter',
        () => {
          this.preloadTryonModules();
        },
        { once: true }
      );
    });
  }

  /**
   * 设置定价模块加载
   */
  setupPricingModuleLoading() {
    const pricingTriggers = document.querySelectorAll(
      '.open-pricing-modal, [data-pricing-trigger]'
    );

    pricingTriggers.forEach(trigger => {
      // 使用标志位而不是once，避免重复加载
      let isLoading = false;
      let isLoaded = false;

      trigger.addEventListener('click', async e => {
        // 如果模块已加载，让事件正常执行
        if (isLoaded) {
          return;
        }

        // 如果正在加载，阻止重复点击
        if (isLoading) {
          e.preventDefault();
          return;
        }

        // 首次点击，需要加载模块
        e.preventDefault();
        isLoading = true;

        try {
          await this.loadPricingModules();
          isLoaded = true;
          isLoading = false;

          // 模块加载完成后，重新触发点击事件
          setTimeout(() => {
            trigger.click();
          }, 100);
        } catch (error) {
          isLoading = false;
          // 定价模块加载失败时的处理
          debugError('[ModuleLoader] Pricing module loading failed in click handler:', error);
        }
      });
    });

    // 预加载策略
    pricingTriggers.forEach(trigger => {
      trigger.addEventListener(
        'mouseenter',
        () => {
          this.preloadPricingModules();
        },
        { once: true }
      );
    });
  }

  /**
   * 设置法律模块加载
   */
  setupLegalModuleLoading() {
    const legalTriggers = document.querySelectorAll('#privacy-policy-link, #terms-of-service-link');

    legalTriggers.forEach(trigger => {
      trigger.addEventListener(
        'click',
        async e => {
          e.preventDefault();

          try {
            await this.loadLegalModules();

            // 重新触发点击事件
            setTimeout(() => {
              trigger.click();
            }, 100);
          } catch (error) {
            // 法律模块加载失败时的处理
            debugError('[ModuleLoader] Legal module loading failed in click handler:', error);
          }
        },
        { once: true }
      );
    });
  }

  /**
   * 加载认证模块
   */
  async loadAuthModules() {
    try {
      return await this.loadModules('auth', [
        () => import(/* webpackChunkName: "auth-modules" */ '../js/modules/auth/social-login.ts'),
        () => import(/* webpackChunkName: "auth-modules" */ '../js/modules/auth/login.js'),
        () => import(/* webpackChunkName: "auth-modules" */ '../js/modules/auth/signup.js'),
        () => import(/* webpackChunkName: "auth-modules" */ '../js/modules/auth/resetpassword.js'),
      ]);
    } catch (error) {
      debugError('[ModuleLoader] Failed to load auth modules:', error);

      if (window.errorHandler) {
        errorHandler.handleError(error, {
          userFriendlyMessage: '登录功能加载失败，请刷新页面重试',
          showUserNotification: true,
        });
      }
      throw error;
    }
  }

  /**
   * 预加载认证模块
   */
  preloadAuthModules() {
    this.loadAuthModules().catch(debugError);
  }

  /**
   * 加载试穿模块
   */
  async loadTryonModules() {
    try {
      return await this.loadModules('tryon', [
        () => import(/* webpackChunkName: "feature-modules" */ '../js/modules/features/tryon.js'),
        () => import(/* webpackChunkName: "feature-modules" */ '../js/modules/ui/image-upload.js'),
      ]);
    } catch (error) {
      debugError('[ModuleLoader] Failed to load tryon modules:', error);

      if (window.errorHandler) {
        errorHandler.handleError(error, {
          userFriendlyMessage: 'AI试穿功能加载失败，请刷新页面重试',
          showUserNotification: true,
        });
      }
      throw error;
    }
  }

  /**
   * 预加载试穿模块
   */
  preloadTryonModules() {
    this.loadTryonModules().catch(debugError);
  }

  /**
   * 加载定价模块
   */
  async loadPricingModules() {
    try {
      return await this.loadModules('pricing', [() => import('../js/modules/ui/pricing-modal.js')]);
    } catch (error) {
      debugError('[ModuleLoader] Failed to load pricing modules:', error);

      if (window.errorHandler) {
        errorHandler.handleError(error, {
          userFriendlyMessage: '定价信息加载失败，请刷新页面重试',
          showUserNotification: true,
        });
      }
      throw error;
    }
  }

  /**
   * 预加载定价模块
   */
  preloadPricingModules() {
    this.loadPricingModules().catch(debugError);
  }

  /**
   * 加载法律模块
   */
  async loadLegalModules() {
    try {
      return await this.loadModules('legal', [() => import('../js/legal-modals.js')]);
    } catch (error) {
      debugError('[ModuleLoader] Failed to load legal modules:', error);

      if (window.errorHandler) {
        errorHandler.handleError(error, {
          userFriendlyMessage: '法律文档加载失败，请刷新页面重试',
          showUserNotification: true,
        });
      }
      throw error;
    }
  }

  /**
   * 通用模块加载器
   */
  async loadModules(moduleGroup, moduleLoaders) {
    if (this.loadedModules.has(moduleGroup)) {
      return;
    }

    if (this.loadingPromises.has(moduleGroup)) {
      return this.loadingPromises.get(moduleGroup);
    }

    const loadingPromise = this.doLoadModules(moduleGroup, moduleLoaders);
    this.loadingPromises.set(moduleGroup, loadingPromise);

    try {
      await loadingPromise;
      this.loadedModules.add(moduleGroup);
      this.loadingPromises.delete(moduleGroup);
      debugInfo(`[ModuleLoader] ${moduleGroup} modules loaded successfully`);
    } catch (error) {
      this.loadingPromises.delete(moduleGroup);
      debugError(`[ModuleLoader] Failed to load ${moduleGroup} modules:`, error);
      throw error;
    }

    return loadingPromise;
  }

  /**
   * 执行模块加载
   */
  async doLoadModules(moduleGroup, moduleLoaders) {
    const startTime = performance.now();

    try {
      await Promise.all(moduleLoaders.map(loader => loader()));

      const endTime = performance.now();
      debugInfo(
        `[ModuleLoader] ${moduleGroup} modules loaded in ${Math.round(endTime - startTime)}ms`
      );

      // 触发模块加载完成事件
      document.dispatchEvent(
        new CustomEvent('moduleLoaded', {
          detail: { moduleGroup, loadTime: endTime - startTime },
        })
      );
    } catch (error) {
      debugError(`[ModuleLoader] Error loading ${moduleGroup} modules:`, error);
      throw error;
    }
  }

  /**
   * 手动加载指定模块组
   */
  async loadModuleGroup(groupName) {
    switch (groupName) {
      case 'auth':
        return this.loadAuthModules();
      case 'tryon':
        return this.loadTryonModules();
      case 'pricing':
        return this.loadPricingModules();
      case 'legal':
        return this.loadLegalModules();
      default:
        debugWarn(`[ModuleLoader] Unknown module group: ${groupName}`);
    }
  }

  /**
   * 获取加载统计信息
   */
  getLoadingStats() {
    return {
      loadedModules: Array.from(this.loadedModules),
      loadingModules: Array.from(this.loadingPromises.keys()),
    };
  }
}

// 创建全局模块加载器实例
window.moduleLoader = new ModuleLoader();

// 性能监控
if (window.performance && window.performance.mark) {
  window.performance.mark('app-start');

  window.addEventListener('load', () => {
    window.performance.mark('app-loaded');
    window.performance.measure('app-load-time', 'app-start', 'app-loaded');

    const loadTime = window.performance.getEntriesByName('app-load-time')[0];
    debugInfo(`[Performance] App loaded in ${Math.round(loadTime.duration)}ms`);
  });
}

// 网络状态监控
window.addEventListener('online', () => {
  debugInfo('[Network] Connection restored');
  // 可以在这里重新尝试失败的请求
});

window.addEventListener('offline', () => {
  debugWarn('[Network] Connection lost - entering offline mode');
  // 可以在这里显示离线提示
});

// 移动端菜单切换
document.addEventListener('DOMContentLoaded', () => {
  const mobileMenuButton = document.getElementById('mobile-menu-button');
  const mobileMenu = document.getElementById('mobile-menu');

  if (mobileMenuButton && mobileMenu) {
    // 封装关闭菜单的函数，统一使用
    const closeMobileMenu = () => {
      const isHidden = mobileMenu.classList.contains('hidden');
      if (!isHidden) {
        mobileMenu.style.opacity = '0';
        mobileMenu.style.transform = 'translateY(-10px)';
        setTimeout(() => {
          mobileMenu.classList.add('hidden');
        }, 300);
        mobileMenuButton.setAttribute('aria-expanded', 'false');
      }
    };

    mobileMenuButton.addEventListener('click', () => {
      const isHidden = mobileMenu.classList.contains('hidden');

      if (isHidden) {
        // 显示菜单 - 先移除hidden类,然后触发动画
        mobileMenu.classList.remove('hidden');
        // 使用requestAnimationFrame确保浏览器渲染后再添加动画
        requestAnimationFrame(() => {
          mobileMenu.style.opacity = '1';
          mobileMenu.style.transform = 'translateY(0)';
        });
        mobileMenuButton.setAttribute('aria-expanded', 'true');
      } else {
        // 隐藏菜单
        closeMobileMenu();
      }
    });

    // 点击菜单链接后自动关闭菜单
    const menuLinks = mobileMenu.querySelectorAll('a');
    menuLinks.forEach(link => {
      link.addEventListener('click', () => {
        closeMobileMenu();
      });
    });

    // 监听页面滚动，菜单展开时自动收起
    let scrollTimeout;
    window.addEventListener(
      'scroll',
      () => {
        const isHidden = mobileMenu.classList.contains('hidden');
        if (!isHidden) {
          // 使用防抖，避免频繁触发
          clearTimeout(scrollTimeout);
          scrollTimeout = setTimeout(() => {
            closeMobileMenu();
          }, 100);
        }
      },
      { passive: true }
    );

    // 监听点击页面其他区域，菜单展开时自动收起
    document.addEventListener('click', event => {
      const isHidden = mobileMenu.classList.contains('hidden');
      if (!isHidden) {
        // 检查点击的是否是菜单按钮或菜单内容
        const isClickInsideMenu = mobileMenu.contains(event.target);
        const isClickMenuButton = mobileMenuButton.contains(event.target);

        // 如果点击的不是菜单按钮也不是菜单内容，则关闭菜单
        if (!isClickInsideMenu && !isClickMenuButton) {
          closeMobileMenu();
        }
      }
    });
  }
});

debugInfo('[App] Main application initialized');
